/**
 * SignalR data types matching the Unity client models
 */

// User & Presence
export interface UserPresence {
  userId: string;
  displayName: string;
  status: PresenceStatus;
  lastSeen: string;
  currentRoomId?: string;
}

export type PresenceStatus = 'online' | 'away' | 'busy' | 'offline';

// Rooms
export interface SignalRRoom {
  id: string;
  code: string;
  name: string;
  description: string;
  hostId: string;
  hostName: string;
  maxPlayers: number;
  currentPlayers: number;
  players: SignalRPlayer[];
  context: string;
  tags: string[];
  isPrivate: boolean;
  hasPassword: boolean;
  requiresApproval: boolean;
  status: RoomStatus;
  createdAt: string;
  settings?: RoomSettings;
  /** P4 catalog taxonomy carried on RoomInfo. */
  category?: string;
  subCategory?: string;
  /** The world this room is built from — used to show the world thumbnail on the card. */
  sceneTemplateId?: string;
  /** Scope: "dating" (default) | "classroom" | "corporate" | "confidence" | "language" (WU-08 R6). */
  mode?: string;
}

export interface RoomSettings {
  minAge?: number;
  maxAge?: number;
  gameMode?: string;
  sceneName?: string;
  customData?: string;
}

export type RoomStatus = 'waiting' | 'in-progress' | 'full' | 'closed';

export type ChatRole = 'system' | 'dungeon_master' | 'user' | 'spectator';

export interface SignalRPlayer {
  id: string;
  displayName: string;
  avatar?: string;
  characterType?: string;
  gender?: string;
  appearanceData?: string;
  isHost: boolean;
  slotIndex: number;
  joinedAt: string;
  presence?: UserPresence;
  // Chat role fields
  chatRole?: ChatRole;
  chatAvailable?: boolean;
}

// Room Events
export interface RoomCreatedEvent {
  room: SignalRRoom;
}

export interface RoomUpdatedEvent {
  room: SignalRRoom;
}

export interface RoomDeletedEvent {
  roomId: string;
}

export interface PlayerJoinedRoomEvent {
  roomId: string;
  player: SignalRPlayer;
}

export interface PlayerLeftRoomEvent {
  roomId: string;
  playerId: string;
}

// Join Request (Knock-to-Enter)
export interface JoinRequestInfo {
  id: string;
  roomId: string;
  requesterId: string;
  requesterDisplayName: string;
  requesterAvatar?: string;
  message?: string;
  requestedAt: string;
}

// Player Sync (matches Unity PlayerSyncData)
export interface PlayerSyncData {
  playerId: string;
  playerName: string;
  characterType: string;
  avatarGuid: string;
  position: Vector3;
  rotation: Quaternion;
  velocity: Vector3;
  timestamp: number;
  sequenceNumber: number;
  state: PlayerState;
}

export type PlayerState = 'idle' | 'moving' | 'jumping' | 'attacking' | 'dead';

export interface Vector3 {
  x: number;
  y: number;
  z: number;
}

export interface Quaternion {
  x: number;
  y: number;
  z: number;
  w: number;
}

// Game State
export interface GameStateData {
  key: string;
  value: string;
  type: GameStateType;
  timestamp: number;
  authority: string;
}

export type GameStateType = 'string' | 'integer' | 'float' | 'boolean' | 'vector3' | 'custom';

// Chat
export interface ChatMessage {
  id: string;
  /** Server wire id (RoomHub ChatMessage.MessageId); mirrored into `id` on receive. */
  messageId?: string;
  senderId: string;
  senderName: string;
  content: string;
  type: ChatMessageType;
  channelId?: string;
  recipients?: string[];
  timestamp: string;
  // Extended fields for room chat
  senderRole?: ChatRole;
  senderAvatar?: string;
  visibility?: 'public' | 'private' | 'selective';
  attachmentUrl?: string;
  thumbnailUrl?: string;
  replyToMessageId?: string;
  replyToPreview?: string;
}

export type ChatMessageType = 'public' | 'private' | 'system' | 'emote';

// ============================================================
// Social economy (Alchemist's Cove P1): Sparks ✦ / inventory / gifts.
// Hub methods return camelCase JSON STRINGS (parse them); GiftOffered /
// EconomyUpdate are TARGETED events sent only to the affected user's
// connections. All additive — old servers simply lack the methods.
// ============================================================

export interface EconomyInventoryItem {
  itemType: string;
  qty: number;
}

export interface EconomyCatalogItem {
  type: string;
  name: string;
  icon: string;
  desc?: string;
  giftable?: boolean;
  /** Throwable effect potion (P2). */
  potion?: boolean;
  /** Sparks to brew one (0/absent = not brewable). */
  brewCost?: number;
}

export interface SocialSettings {
  /** 0 = everyone, 1 = people I liked (default), 2 = nobody */
  effectPotions: number;
  /** Who may ring me for an in-room call: 0 = everyone (default — the
   * ring itself still needs accepting), 1 = people I liked, 2 = nobody. */
  calls?: number;
}

export interface EconomySnapshot {
  ok: boolean;
  error?: string;
  userId?: string;
  sparks: number;
  inventory: EconomyInventoryItem[];
  catalog: EconomyCatalogItem[];
  settings?: SocialSettings;
  /** Data-driven tuning echoed for UI copy (never hardcode these numbers client-side). */
  config?: {
    giftGiverSparksOnAccept: number;
    giftReceiverSparksOnAccept: number;
    giftCooldownSecondsPerTarget: number;
    giftDailyLimitPerTarget: number;
    giftOfferExpirySeconds: number;
  };
}

export interface SendGiftResult {
  ok: boolean;
  error?: string;
  retryInSeconds?: number;
  giftId?: string;
  inventory?: EconomyInventoryItem[];
}

export interface RespondToGiftResult {
  ok: boolean;
  error?: string;
  sparks?: number;
  inventory?: EconomyInventoryItem[];
}

export interface GiftOfferedEvent {
  giftId: string;
  roomId: string;
  fromUserId: string;
  fromName: string;
  itemType: string;
  itemName: string;
  icon: string;
  expiresAt: string;
  receiverSparksOnAccept: number;
}

export interface EconomyUpdateEvent {
  userId: string;
  sparks: number;
  inventory: EconomyInventoryItem[];
  sparksDelta?: number;
  reason?: string;
  detail?: {
    giftId?: string;
    throwId?: string;
    itemType?: string;
    itemName?: string;
    fromName?: string;
    toName?: string;
  };
}

// ---- Social events (P2 potions & throwing; P4 puzzle deltas) ----

/** Server-stamped SocialEventReceived envelope; payloadJson is type-specific JSON. */
export interface SocialEventEnvelope {
  type: string;
  from: string;
  fromName: string;
  to: string;
  toName: string;
  ts: string;
  payloadJson: string;
}

export interface PotionThrowBroadcast {
  throwId: string;
  potionType: string;
  itemName: string;
  icon: string;
  effect: { kind: string; color?: string; durationSeconds?: number; scale?: number };
  cheersSparks: number;
  cheersWindowSeconds: number;
}

export interface ThrowTarget {
  userId: string;
  displayName: string;
  allowed: boolean;
}

export interface ThrowTargetsResult {
  ok: boolean;
  error?: string;
  targets?: ThrowTarget[];
}

// Room Chat Types
export interface RoomChatSettings {
  allowImages: boolean;
  allowGifs: boolean;
  allowEmotes: boolean;
  allowPrivateMessages: boolean;
  moderationLevel: 'none' | 'light' | 'strict';
  slowModeSeconds: number;
}

export interface MuteInfo {
  oderId: string;
  moderatorName: string;
  reason?: string;
  expiresAt?: string;
  createdAt: string;
}

// Friends
export interface Friend {
  userId: string;
  displayName: string;
  avatar?: string;
  presence: UserPresence;
  addedAt: string;
}

export interface FriendRequest {
  id: string;
  fromUserId: string;
  fromDisplayName: string;
  fromAvatar?: string;
  message?: string;
  sentAt: string;
}

// Matchmaking
export interface MatchmakingRequest {
  gameMode: string;
  minPlayers?: number;
  maxPlayers?: number;
  preferences?: MatchmakingPreferences;
}

export interface MatchmakingPreferences {
  minAge?: number;
  maxAge?: number;
  interests?: string[];
  region?: string;
}

export interface MatchmakingResult {
  matchId: string;
  roomId: string;
  players: SignalRPlayer[];
  gameMode: string;
}

export type MatchmakingStatus = 'idle' | 'searching' | 'found' | 'accepted' | 'declined' | 'timeout';

// User Profile (for registration/auth)
export interface UserProfile {
  id: string;
  displayName: string;
  email?: string;
  avatar?: string;
  bio?: string;
  age?: number;
  gender?: string;
  characterClass?: string;
  interests?: string[];
  createdAt: string;
  lastActive: string;
}

export interface RegisterUserRequest {
  displayName: string;
  email?: string;
  password?: string;
  avatar?: string;
  bio?: string;
  age?: number;
  gender?: string;
  characterClass?: string;
  interests?: string[];
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface AuthResponse {
  success: boolean;
  user?: UserProfile;
  token?: string;
  error?: string;
  // Additive: server-supplied reason code so the UI can branch on the rejection
  // cause. 'error' = server/transport fault (distinct from bad credentials);
  // 'missing_field' | 'weak_password' | 'email_taken' = register validation failures.
  // Absent on success / old servers. Open `| string` kept for forward-compat with new codes.
  reason?: 'invalid' | 'banned' | 'suspended' | 'error' | 'missing_field' | 'weak_password' | 'email_taken' | string;
}

export interface RegisterUserResponse {
  success: boolean;
  user?: UserProfile;
  error?: string;
}

// Hub method names for type safety
export const HubMethods = {
  // User
  Login: 'Login',
  RegisterUser: 'RegisterUser',
  ReconnectUser: 'ReconnectUser',
  UpdateProfile: 'UpdateProfile',
  GetUserProfile: 'GetUserProfile',

  // Presence
  UpdatePresence: 'UpdatePresence',
  GetOnlineUsers: 'GetOnlineUsers',

  // Rooms
  CreateRoom: 'CreateRoom',
  JoinRoom: 'JoinRoom',
  LeaveRoom: 'LeaveRoom',
  GetRooms: 'GetRooms',
  GetRoom: 'GetRoom',
  UpdateRoom: 'UpdateRoom',
  DeleteRoom: 'DeleteRoom',

  // Join Requests (Knock-to-Enter)
  RequestToJoin: 'RequestToJoin',
  ApproveJoinRequest: 'ApproveJoinRequest',
  DenyJoinRequest: 'DenyJoinRequest',
  GetPendingJoinRequests: 'GetPendingJoinRequests',
  CancelJoinRequest: 'CancelJoinRequest',

  // Game
  JoinGame: 'JoinGame',
  LeaveGame: 'LeaveGame',
  SyncPlayer: 'SyncPlayer',
  UpdateGameState: 'UpdateGameState',
  SendNetworkPayload: 'SendNetworkPayload',

  // Chat
  SendChatMessage: 'SendChatMessage',
  SendPrivateMessage: 'SendPrivateMessage',
  JoinChatChannel: 'JoinChatChannel',
  LeaveChatChannel: 'LeaveChatChannel',
  GetRoomChatHistory: 'GetRoomChatHistory',

  // Social economy (Sparks ✦ / inventory / gifts / consent)
  GetMyEconomy: 'GetMyEconomy',
  SendGift: 'SendGift',
  RespondToGift: 'RespondToGift',
  SetSocialConsent: 'SetSocialConsent',
  SetCallConsent: 'SetCallConsent',

  // Social events (potions & throwing, P2; calls)
  SocialEvent: 'SocialEvent',
  GetThrowTargets: 'GetThrowTargets',
  GetCallTargets: 'GetCallTargets',
  BrewPotion: 'BrewPotion',

  // Room Chat (extended)
  SendRoomChatMessage: 'SendRoomChatMessage',
  SendRoomTypingIndicator: 'SendRoomTypingIndicator',
  MarkRoomMessagesRead: 'MarkRoomMessagesRead',
  UpdateChatAvailability: 'UpdateChatAvailability',
  SetPlayerChatRole: 'SetPlayerChatRole',
  MutePlayer: 'MutePlayer',
  UnmutePlayer: 'UnmutePlayer',
  DeleteRoomMessage: 'DeleteRoomMessage',
  BlockUserInRoomChat: 'BlockUserInRoomChat',
  UnblockUserInRoomChat: 'UnblockUserInRoomChat',

  // Friends
  SendFriendRequest: 'SendFriendRequest',
  AcceptFriendRequest: 'AcceptFriendRequest',
  DeclineFriendRequest: 'DeclineFriendRequest',
  RemoveFriend: 'RemoveFriend',
  GetFriendList: 'GetFriendList',

  // Matchmaking
  StartMatchmaking: 'StartMatchmaking',
  CancelMatchmaking: 'CancelMatchmaking',
  AcceptMatch: 'AcceptMatch',
  DeclineMatch: 'DeclineMatch',

  // Host control plane (WU-08 R1/R2) — grant-checked hub methods, RoomHub.Host.cs
  JoinHostConsole: 'JoinHostConsole',
  HostBroadcast: 'HostBroadcast',
  HostWhisper: 'HostWhisper',
  HostAssignMission: 'HostAssignMission',
  HostPauseRoom: 'HostPauseRoom',
  HostResume: 'HostResume',
  HostSummonAll: 'HostSummonAll',
  HostSpotlight: 'HostSpotlight',
  HostLockZone: 'HostLockZone',
  HostEndSession: 'HostEndSession',
} as const;

// Hub event names for type safety
export const HubEvents = {
  // User
  UserRegistered: 'UserRegistered',
  ProfileUpdated: 'ProfileUpdated',

  // Presence
  PresenceUpdated: 'PresenceUpdated',
  UserOnline: 'UserOnline',
  UserOffline: 'UserOffline',

  // Rooms
  RoomCreated: 'RoomCreated',
  RoomUpdated: 'RoomUpdated',
  RoomDeleted: 'RoomDeleted',
  RoomListUpdated: 'RoomListUpdated',
  PlayerJoinedRoom: 'PlayerJoinedRoom',
  PlayerLeftRoom: 'PlayerLeftRoom',

  // Join Requests (Knock-to-Enter)
  JoinRequestReceived: 'JoinRequestReceived',
  JoinRequestApproved: 'JoinRequestApproved',
  JoinRequestDenied: 'JoinRequestDenied',
  JoinRequestCancelled: 'JoinRequestCancelled',

  // Game
  PlayerJoinedGame: 'PlayerJoinedGame',
  PlayerLeftGame: 'PlayerLeftGame',
  PlayerSpawned: 'PlayerSpawned',
  ReceivePlayerSync: 'ReceivePlayerSync',
  GameStateUpdate: 'GameStateUpdate',
  ReceiveNetworkPayload: 'ReceiveNetworkPayload',
  PlayerDisconnected: 'PlayerDisconnected',

  // Chat
  ReceiveChatMessage: 'ReceiveChatMessage',
  ChatChannelJoined: 'ChatChannelJoined',
  ChatChannelLeft: 'ChatChannelLeft',

  // Social economy (targeted to the affected user's connections)
  GiftOffered: 'GiftOffered',
  EconomyUpdate: 'EconomyUpdate',
  // Social events (room-group broadcast, server-stamped envelope)
  SocialEventReceived: 'SocialEventReceived',

  // Room Chat (extended)
  ReceiveRoomChatMessage: 'ReceiveRoomChatMessage',
  RoomMessageDeleted: 'RoomMessageDeleted',
  RoomTypingIndicator: 'RoomTypingIndicator',
  RoomChatHistory: 'RoomChatHistory',
  PlayerChatRoleChanged: 'PlayerChatRoleChanged',
  PlayerChatAvailabilityChanged: 'PlayerChatAvailabilityChanged',
  PlayerMuted: 'PlayerMuted',
  PlayerUnmuted: 'PlayerUnmuted',
  RoomChatSettingsChanged: 'RoomChatSettingsChanged',

  // Friends
  FriendRequestReceived: 'FriendRequestReceived',
  FriendRequestAccepted: 'FriendRequestAccepted',
  FriendAdded: 'FriendAdded',
  FriendRemoved: 'FriendRemoved',
  FriendPresenceChanged: 'FriendPresenceChanged',

  // Matchmaking
  MatchFound: 'MatchFound',
  MatchmakingStatusChanged: 'MatchmakingStatusChanged',
  MatchCompleted: 'MatchCompleted',
} as const;
